import { S } from './state.js';
import { Audio } from './audio.js';
import { fetchKnobs } from './config.js';
import { DEV } from './data/progression.js';
import { hintEl, keys, reset } from './main.js';
import { persist } from './economy.js';
import { tryDash } from './allies.js';
import { devCommand } from './meter.js';
import { cv } from './canvas.js';
import { buyShrineDraft, closeShrineDraft, confirmBlood, confirmPandora, rerollBossReward, takeBossReward, takeLevelUp, tryInteract } from './shrines.js';
// ---- Input ----
S.holding = false;
export const aim = { x: S.W / 2, y: S.H / 2 };
export function pointer(e) {
  const t = e.touches ? e.touches[0] : e;
  aim.x = t.clientX; aim.y = t.clientY;
}
export function down(e) {
  e.preventDefault();
  Audio.resume();   // browsers require a user gesture before audio can start
  pointer(e);
  if (!S.started || S.dead || S.paused || S.shopOpen || S.showJournal || S.showTalents || S.showCharacter || S.bloodConfirm || S.pandoraConfirm || S.bossReward || S.levelUp || S.shrineDraft) { handleMenuClick(aim.x, aim.y); return; }
  S.holding = true;   // hold to shoot toward the cursor
}
export function up() { S.holding = false; }

export function pointIn(p, x, y, w, h) { return p.x >= x && p.x <= x + w && p.y >= y && p.y <= y + h; }
export function handleMenuClick(mx, my) {
  for (const b of S.uiButtons) if (pointIn({ x: mx, y: my }, b.x, b.y, b.w, b.h)) { Audio.play('click'); b.action(); return; }
}
export function startRun() {
  Audio.resume(); S.showLeaderboard = false;
  fetchKnobs();   // pick up any live-tuning edits (async — applies as soon as it lands; see the knobs block up top)
  if (S.updateReady) {   // a new build is live — swap it in NOW and auto-start, so the player just sees a blink
    try { sessionStorage.setItem('vg_autostart', '1'); } catch (e) {}
    location.reload(); return;
  }
  reset(); S.started = true;
}

cv.addEventListener('mousedown', down);
window.addEventListener('mouseup', up);
window.addEventListener('mousemove', pointer);

// ============================ TOUCH CONTROLS (0.18) ============================
// The old handlers read e.touches[0] — single-touch only, so you could move OR shoot. Phones need
// both at once: a multi-touch router where each touch is CLAIMED by exactly one job and tracked by
// identifier. Priority: on-screen button > left-zone joystick > aim/fire. preventDefault on
// touchstart also suppresses the synthetic mouse events, so the mouse path above stays untouched.
export const TOUCH_CAPABLE = (() => { try { return matchMedia('(pointer: coarse)').matches; } catch (e) { return false; } })();
S.isTouch = TOUCH_CAPABLE;  // confirmed by the first real touch — gates the on-screen controls
export const joy = { id: null, ox: 0, oy: 0, dx: 0, dy: 0 };   // virtual stick — claims touches starting on the left 45%
S.aimTouchId = null;        // the touch currently steering aim + holding
S.touchBtns = [];           // rebuilt each frame by drawTouchControls(): {x, y, r, action}
S.jDrag = null;             // journal drag-scroll state {id, y}
export const JOY_SAT = 56;           // px of thumb travel = full speed (analog below that)
export function touchOnboard() {     // one-time mobile onboarding: auto-aim ON + a touch-worded hint
  S.isTouch = true;
  if (!S.save.settings._touchInit) {
    S.save.settings._touchInit = true;
    S.save.settings.autoAim = true;   // one thumb moves, the gun aims itself — change it in the pause menu
    persist();
  }
  if (hintEl && !hintEl._touchWorded) {
    hintEl._touchWorded = true;
    hintEl.innerHTML = '<b>drag LEFT side</b> move &nbsp;•&nbsp; <b>drag RIGHT side</b> aim &amp; shoot &nbsp;•&nbsp; auto-aim is <b>ON</b> &nbsp;•&nbsp; walk into 🛒 caches &nbsp;•&nbsp; <b>E button</b> grab weapons / use shrines';
  }
}
cv.addEventListener('touchstart', e => {
  e.preventDefault();
  Audio.resume();
  touchOnboard();
  const menuUp = !S.started || S.dead || S.paused || S.shopOpen || S.showJournal || S.showTalents || S.showCharacter || S.bloodConfirm || S.pandoraConfirm || S.bossReward || S.levelUp || S.shrineDraft;
  for (const t of e.changedTouches) {
    if (menuUp) {
      if (S.showJournal || S.showTalents || S.showCharacter) S.jDrag = { id: t.identifier, y: t.clientY };   // journal/talents/character: drag scrolls, taps still click
      handleMenuClick(t.clientX, t.clientY);
      continue;
    }
    let claimed = false;
    for (const b of S.touchBtns) {                              // (1) on-screen buttons
      if (Math.hypot(t.clientX - b.x, t.clientY - b.y) < b.r + 10) { Audio.play('click'); b.action(); claimed = true; break; }
    }
    if (claimed) continue;
    if (t.clientX < S.W * 0.45 && joy.id === null) {            // (2) left zone → movement stick
      joy.id = t.identifier; joy.ox = t.clientX; joy.oy = t.clientY; joy.dx = joy.dy = 0;
      continue;
    }
    if (S.aimTouchId === null) {                                // (3) the rest → manual aim + fire (wins over auto-aim)
      S.aimTouchId = t.identifier;
      aim.x = t.clientX; aim.y = t.clientY;
      S.holding = true;
    }
  }
}, { passive: false });
cv.addEventListener('touchmove', e => {
  e.preventDefault();
  for (const t of e.changedTouches) {
    if (t.identifier === joy.id) {
      let dx = t.clientX - joy.ox, dy = t.clientY - joy.oy;
      const d = Math.hypot(dx, dy);
      if (d > JOY_SAT) { dx = dx / d * JOY_SAT; dy = dy / d * JOY_SAT; }   // saturation: full tilt
      else if (d < 10) dx = dy = 0;                                       // dead zone: resting thumb ≠ drift
      joy.dx = dx; joy.dy = dy;
    } else if (t.identifier === S.aimTouchId) {
      aim.x = t.clientX; aim.y = t.clientY;
    } else if (S.jDrag && t.identifier === S.jDrag.id && S.showJournal) {
      S.journalScroll = Math.max(0, Math.min(S.journalScrollMax, S.journalScroll - (t.clientY - S.jDrag.y)));
      S.jDrag.y = t.clientY;
    } else if (S.jDrag && t.identifier === S.jDrag.id && S.showTalents) {
      S.talentScroll = Math.max(0, Math.min(S.talentScrollMax, S.talentScroll - (t.clientY - S.jDrag.y)));
      S.jDrag.y = t.clientY;
    } else if (S.jDrag && t.identifier === S.jDrag.id && S.showCharacter) {
      S.charScroll = Math.max(0, Math.min(S.charScrollMax, S.charScroll - (t.clientY - S.jDrag.y)));
      S.jDrag.y = t.clientY;
    }
  }
}, { passive: false });
export function touchEnd(e) {
  for (const t of e.changedTouches) {
    if (t.identifier === joy.id) { joy.id = null; joy.dx = joy.dy = 0; }
    if (t.identifier === S.aimTouchId) { S.aimTouchId = null; S.holding = false; }
    if (S.jDrag && t.identifier === S.jDrag.id) S.jDrag = null;
  }
}
window.addEventListener('touchend', touchEnd);
window.addEventListener('touchcancel', touchEnd);   // alt-tab / notification swipe mid-drag must not leave `holding` stuck

export function setKey(e, v) {
  switch (e.key) {
    case 'w': case 'W': case 'ArrowUp':    keys.up = v; return true;
    case 's': case 'S': case 'ArrowDown':  keys.down = v; return true;
    case 'a': case 'A': case 'ArrowLeft':  keys.left = v; return true;
    case 'd': case 'D': case 'ArrowRight': keys.right = v; return true;
  }
  return false;
}
window.addEventListener('keydown', e => {
  Audio.resume();
  if (S.bloodConfirm) {   // Blood Shrine prompt is up — answer it, swallow everything else
    if (e.key === 'y' || e.key === 'Y' || e.key === 'Enter') confirmBlood();
    else if (e.key === 'n' || e.key === 'N' || e.key === 'Escape') S.bloodConfirm = null;
    e.preventDefault(); return;
  }
  if (S.pandoraConfirm) {   // Pandora's Box prompt is up — same deal
    if (e.key === 'y' || e.key === 'Y' || e.key === 'Enter') confirmPandora();
    else if (e.key === 'n' || e.key === 'N' || e.key === 'Escape') S.pandoraConfirm = null;
    e.preventDefault(); return;
  }
  if (S.bossReward) {   // boss reward draft is up — pick one with 1 / 2 / 3, or R to pay for a fresh set
    if (e.key === '1') takeBossReward(0);
    else if (e.key === '2') takeBossReward(1);
    else if (e.key === '3') takeBossReward(2);
    else if (e.key === '4') takeBossReward(3);   // Architect's Pen 4th choice
    else if ((e.key === 'r' || e.key === 'R') && !e.repeat) rerollBossReward();
    e.preventDefault(); return;
  }
  if (S.levelUp) {   // level-up stat draft is up — pick one with 1 / 2 / 3
    if (e.key === '1') takeLevelUp(0);
    else if (e.key === '2') takeLevelUp(1);
    else if (e.key === '3') takeLevelUp(2);
    else if (e.key === '4') takeLevelUp(3);   // Architect's Pen 4th choice
    e.preventDefault(); return;
  }
  if (S.shrineDraft) {   // item cache is open — buy with 1 / 2 / 3, or walk away (E / Esc / N)
    if (e.key === '1') buyShrineDraft(0);
    else if (e.key === '2') buyShrineDraft(1);
    else if (e.key === '3') buyShrineDraft(2);
    else if (e.key === '4') buyShrineDraft(3);   // Architect's Pen 4th choice
    else if (e.key === 'Escape' || e.key === 'n' || e.key === 'N' || ((e.key === 'e' || e.key === 'E') && !e.repeat)) closeShrineDraft();
    e.preventDefault(); return;
  }
  if (DEV && e.key === '`') { devCommand(); e.preventDefault(); return; }
  if (setKey(e, true)) { e.preventDefault(); return; }
  if (e.key === ' ' || e.key === 'Spacebar') {        // Space: start/retry, or dash mid-run
    e.preventDefault();
    if (S.showLeaderboard) { S.showLeaderboard = false; return; }
    // Space is also the DASH key — a 1.5s grace after death stops panic-mashing from skipping
    // the Run Over screen straight into a new game (TODO fix, 0.16)
    if (!S.started) startRun();
    else if (S.dead) { if (Date.now() - S.deathAt > 1500) startRun(); }
    else tryDash();
    return;
  }
  if (e.key === 'Enter' && (!S.started || (S.dead && Date.now() - S.deathAt > 1500))) startRun();
  if (e.key === 'm' || e.key === 'M') Audio.toggle();
  if (e.key === 'f' || e.key === 'F') { S.save.settings.reduceFlash = !S.save.settings.reduceFlash; persist(); }
  if (e.key === 'k' || e.key === 'K') { S.save.settings.dmgMeter = !S.save.settings.dmgMeter; persist(); }   // toggle the live damage meter
  if (e.key === 'b' || e.key === 'B') { S.save.settings.statsPanel = !S.save.settings.statsPanel; persist(); }   // toggle the live stats panel
  if (e.key === 't' || e.key === 'T') { S.save.settings.autoAim = !S.save.settings.autoAim; persist(); }         // toggle auto-aim (also a pause-menu toggle)
  if ((e.key === 'e' || e.key === 'E') && !e.repeat) tryInteract();   // grab a weapon / use a shrine — !e.repeat so a held key can't machine-gun gamble rerolls
  if ((e.key === 'l' || e.key === 'L') && (!S.started || S.dead)) S.showLeaderboard = !S.showLeaderboard;   // toggle leaderboard on a menu
  if (e.key === 'Escape' && S.showLeaderboard) { S.showLeaderboard = false; return; }   // back out of the leaderboard
  if (e.key === 'j' || e.key === 'J') { S.showJournal = !S.showJournal; S.journalScroll = 0; return; }   // toggle the item journal
  if (e.key === 'Escape' && S.showJournal) { S.showJournal = false; return; }            // back out of the journal
  if (e.key === 'c' || e.key === 'C') { S.showCharacter = !S.showCharacter; S.charScroll = 0; S.charPickSlot = null; return; }   // toggle the Character Panel
  if (e.key === 'Escape' && S.showCharacter) { S.showCharacter = false; return; }        // back out of the Character Panel
  if ((e.key === 'n' || e.key === 'N') && S.started && !S.dead) { S.showTalents = !S.showTalents; S.talentScroll = 0; return; }   // toggle the talent tree
  if (e.key === 'Escape' && S.showTalents) { S.showTalents = false; return; }            // back out of the talent tree
  if ((e.key === 'Escape' || e.key === 'p' || e.key === 'P') && S.started && !S.dead && !S.shopOpen) S.paused = !S.paused;
});
window.addEventListener('keyup', e => { setKey(e, false); });
window.addEventListener('wheel', e => {   // scroll the item journal / talent tree when open
  if (S.showJournal) S.journalScroll = Math.max(0, Math.min(S.journalScrollMax, S.journalScroll + e.deltaY));
  else if (S.showTalents) S.talentScroll = Math.max(0, Math.min(S.talentScrollMax, S.talentScroll + e.deltaY));
  else if (S.showCharacter) S.charScroll = Math.max(0, Math.min(S.charScrollMax, S.charScroll + e.deltaY));
  else return;
  e.preventDefault();
}, { passive: false });
